"use client"

import { useState } from "react"
import {
  X,
  Package,
  Hash,
  Code2,
  Layers,
  Scale,
  Archive,
  DollarSign,
  TrendingUp,
  Save
} from "lucide-react"

const ProductModal = ({ onClose, onSubmit, initialData = null }) => {
  const [formData, setFormData] = useState({
    itemNo: initialData?.itemNo || "",
    baseCode: initialData?.baseCode || "",
    productType: initialData?.productType || "",
    productName: initialData?.productName || "",
    variation: initialData?.variation || "",
    stock: initialData?.stock || "",
    mrp: initialData?.mrp || "",
    costPrice: initialData?.costPrice || "",
  })
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)

  const { itemNo, baseCode, productType, productName, variation, stock, mrp, costPrice } = formData

  const onChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validate form
    if (!itemNo || !baseCode || !productType || !productName || !variation || !stock || !mrp) {
      setError("All fields (except Cost Price) are required")
      return
    }

    // Validate MRP is a positive number
    if (isNaN(mrp) || Number(mrp) <= 0) {
      setError("MRP must be a positive number")
      return
    }

    setLoading(true)
    const success = await onSubmit({
      ...formData,
      mrp: Number(mrp),
      costPrice: Number(costPrice || 0),
    })
    setLoading(false)

    if (success) {
      onClose()
    }
  }

  const InputField = ({ label, name, value, onChange, icon: Icon, type = "text", placeholder, required = true, subLabel, className = "" }) => (
    <div className={className}>
      <label htmlFor={name} className="block text-sm font-semibold text-gray-700 mb-1.5 flex justify-between items-center">
        {label}
        {subLabel && <span className="text-xs font-normal text-gray-400 italic">{subLabel}</span>}
      </label>
      <div className="relative group">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Icon className="h-4 w-4 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
        </div>
        <input
          id={name}
          name={name}
          type={type}
          required={required}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          step={type === "number" ? "0.01" : undefined}
          min={type === "number" ? "0" : undefined}
          className="block w-full pl-10 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium text-gray-900 placeholder-gray-400 sm:text-sm"
        />
      </div>
    </div>
  )

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <div>
            <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              {initialData ? <Package className="w-5 h-5 text-blue-600" /> : <Package className="w-5 h-5 text-green-600" />}
              {initialData ? "Edit Product" : "Add New Product"}
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {initialData ? "Update product details and inventory." : "Enter details to create a new product inventory."}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all focus:outline-none"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body (Scrollable) */}
        <div className="overflow-y-auto p-6 md:p-8 custom-scrollbar">
          <form id="product-form" onSubmit={handleSubmit}>
            {error && (
              <div className="mb-6 bg-red-50 border border-red-100 text-red-600 px-4 py-3 rounded-xl text-sm font-medium flex items-center gap-2">
                <X className="w-4 h-4" /> {error}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Product Identity */}
              <div className="md:col-span-2">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-100 pb-2">
                  Product Identity
                </h4>
              </div>

              <InputField
                label="Product Name"
                name="productName"
                value={productName}
                onChange={onChange}
                icon={Package}
                className="md:col-span-2"
                placeholder="e.g. Lavender Face Wash"
              />

              <InputField
                label="Item No"
                name="itemNo"
                value={itemNo}
                onChange={onChange}
                icon={Hash}
                placeholder="e.g. 1001"
              />

              <InputField
                label="Base Code"
                name="baseCode"
                value={baseCode}
                onChange={onChange}
                icon={Code2}
                placeholder="e.g. FW-LAV"
              />

              <InputField
                label="Product Type"
                name="productType"
                value={productType}
                onChange={onChange}
                icon={Layers}
                placeholder="e.g. Skin Care"
              />

              <InputField
                label="Variation"
                name="variation"
                value={variation}
                onChange={onChange}
                icon={Archive}
                placeholder="e.g. 100ml"
                subLabel="Size, Color, etc."
              />

              {/* Pricing & Inventory */}
              <div className="md:col-span-2 mt-2">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-100 pb-2">
                  Pricing & Inventory
                </h4>
              </div>

              <InputField
                label="MRP (Selling Price)"
                name="mrp"
                value={mrp}
                onChange={onChange}
                icon={DollarSign}
                type="number"
                placeholder="0.00"
              />

              <InputField
                label="Cost Price"
                name="costPrice"
                value={costPrice}
                onChange={onChange}
                icon={TrendingUp}
                type="number"
                placeholder="0.00"
                required={false}
                subLabel="For profit calculation"
              />

              <div className="md:col-span-2">
                <label htmlFor="stock" className="block text-sm font-semibold text-gray-700 mb-1.5 is-required">
                  Initial Stock Level
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Scale className="h-4 w-4 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                  </div>
                  <input
                    id="stock"
                    name="stock"
                    type="number"
                    min="0"
                    required
                    value={stock}
                    onChange={onChange}
                    className="block w-full pl-10 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium text-gray-900 placeholder-gray-400 sm:text-sm"
                    placeholder="Enter quantity"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span className="text-gray-400 text-xs font-medium">Units</span>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 border border-gray-200 text-gray-600 font-medium rounded-xl hover:bg-white hover:border-gray-300 hover:shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-gray-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="product-form"
            disabled={loading}
            className="px-6 py-2.5 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 shadow-lg shadow-blue-600/20 transition-all focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {initialData ? "Update Product" : "Save Product"}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ProductModal
